import React, { useState, useEffect, useMemo } from 'react'
import { Save, Loader2, Zap, AlertCircle, X, Package } from 'lucide-react'
import { Drawer } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import useActivationStore from '../../stores/activationStore'
import useActivationTypeStore from '../../stores/activationTypeStore'
import usePartnerStore from '../../stores/partnerStore'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import { Activation, ActivationStatus } from '../../types/activation'
import { Asset } from '../../types/asset'
import { assetService } from '../../services/assetService'
import { activationService } from '../../services/activationService'
import CPMSlider from '../planner/CPMSlider'

interface EditActivationDrawerProps {
  isOpen: boolean
  onClose: () => void
  activation: Activation | null
}

const EditActivationDrawer: React.FC<EditActivationDrawerProps> = ({
  isOpen,
  onClose,
  activation
}) => {
  const { showSuccess, showError } = useToast()
  const { updateActivation, setActivationAssets, loadActivations } = useActivationStore()
  const { activationTypes, loadActivationTypes } = useActivationTypeStore()
  const { partners, loadPartners } = usePartnerStore()
  const { selectedCustomer } = useGlobalCustomerStore()

  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [partnerId, setPartnerId] = useState('')
  const [selectedAssetIds, setSelectedAssetIds] = useState<string[]>([])
  const [activationTypeId, setActivationTypeId] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [dealValue, setDealValue] = useState('')
  const [emvTargetValue, setEmvTargetValue] = useState('')
  const [notes, setNotes] = useState('')
  const [status, setStatus] = useState<ActivationStatus>('Pending')
  const [isUpdating, setIsUpdating] = useState(false)

  // CPM Multipliers (stored as rate per engagement, e.g., 0.004 = $4.00 CPM)
  const [viewsCpm, setViewsCpm] = useState(0.004)
  const [likesCpm, setLikesCpm] = useState(0.006)
  const [commentsCpm, setCommentsCpm] = useState(0.008)
  const [sharesCpm, setSharesCpm] = useState(0.01)

  // Assets state
  const [assets, setAssets] = useState<Asset[]>([])
  const [isLoadingAssets, setIsLoadingAssets] = useState(false)

  // Full activation with assets
  const [fullActivation, setFullActivation] = useState<Activation | null>(null)

  useEffect(() => {
    if (isOpen && selectedCustomer?.id) {
      loadActivationTypes(String(selectedCustomer.id), true)
      loadPartners(String(selectedCustomer.id), undefined, 'Active', undefined, undefined, 1, 100)
      loadAssets()
    }
  }, [isOpen, selectedCustomer?.id, loadActivationTypes, loadPartners])

  // Fetch full activation with assets when drawer opens
  useEffect(() => {
    const fetchFullActivation = async () => {
      if (!isOpen || !activation?.id || !selectedCustomer?.id) {
        setFullActivation(null)
        return
      }

      try {
        const full = await activationService.getActivation(activation.id, String(selectedCustomer.id))
        setFullActivation(full)
      } catch (error) {
        console.error('Failed to fetch full activation:', error)
        setFullActivation(null)
      }
    }

    fetchFullActivation()
  }, [isOpen, activation?.id, selectedCustomer?.id])

  const loadAssets = async () => {
    if (!selectedCustomer?.id) return
    setIsLoadingAssets(true)
    try {
      const response = await assetService.getAssets(
        String(selectedCustomer.id),
        'Active',
        undefined,
        undefined,
        undefined,
        1,
        100
      )
      setAssets(response.data)
    } catch (error) {
      console.error('Failed to load assets:', error)
    } finally {
      setIsLoadingAssets(false)
    }
  }

  // Load basic activation data
  useEffect(() => {
    if (activation) {
      setName(activation.name)
      setDescription(activation.description || '')
      setPartnerId(activation.partner_id || '')
      setActivationTypeId(activation.activation_type_id || '')
      setStartDate(activation.start_date ? activation.start_date.split('T')[0] : '')
      setEndDate(activation.end_date ? activation.end_date.split('T')[0] : '')
      setDealValue(activation.deal_value?.toString() || '')
      setEmvTargetValue(activation.emv_target_value?.toString() || '')
      setNotes(activation.notes || '')
      setStatus(activation.status)
      // Load CPM values from metadata
      setViewsCpm(activation.metadata?.views_cpm ?? 0.004)
      setLikesCpm(activation.metadata?.likes_cpm ?? 0.006)
      setCommentsCpm(activation.metadata?.comments_cpm ?? 0.008)
      setSharesCpm(activation.metadata?.shares_cpm ?? 0.01)
    }
  }, [activation])

  // Load asset IDs from full activation (which includes activation_assets)
  useEffect(() => {
    if (!fullActivation) {
      setSelectedAssetIds([])
      return
    }

    const existingAssets = fullActivation.activation_assets || []
    // Extract asset IDs - use assets.id if asset_id is not directly available
    const assetIds = existingAssets
      .map((aa) => aa.asset_id || aa.assets?.id)
      .filter((id): id is string => Boolean(id))

    setSelectedAssetIds(assetIds)
  }, [fullActivation])

  const resetForm = () => {
    setName('')
    setDescription('')
    setPartnerId('')
    setSelectedAssetIds([])
    setActivationTypeId('')
    setStartDate('')
    setEndDate('')
    setDealValue('')
    setEmvTargetValue('')
    setNotes('')
    setStatus('Pending')
    setViewsCpm(0.004)
    setLikesCpm(0.006)
    setCommentsCpm(0.008)
    setSharesCpm(0.01)
    setFullActivation(null)
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleAssetToggle = (assetId: string) => {
    setSelectedAssetIds((prev) =>
      prev.includes(assetId)
        ? prev.filter((id) => id !== assetId)
        : [...prev, assetId]
    )
  }

  const handleRemoveAsset = (assetId: string) => {
    setSelectedAssetIds((prev) => prev.filter((id) => id !== assetId))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!activation || !selectedCustomer?.id) {
      showError('Error', 'Missing required data')
      return
    }

    if (!name.trim()) {
      showError('Validation Error', 'Activation name is required')
      return
    }

    if (name.length > 255) {
      showError('Validation Error', 'Activation name must be 255 characters or less')
      return
    }

    if (!partnerId) {
      showError('Validation Error', 'Partner is required')
      return
    }

    // Validate required fields for Active status
    if (status === 'Active') {
      const missingFields: string[] = []
      if (selectedAssetIds.length === 0) missingFields.push('At least one Asset')
      if (!activationTypeId) missingFields.push('Activation Type')
      if (!startDate) missingFields.push('Start Date')
      if (!endDate) missingFields.push('End Date')

      if (missingFields.length > 0) {
        showError(
          'Validation Error',
          `To set status to Active, the following are required: ${missingFields.join(', ')}`
        )
        return
      }

      // Check if start date is in the past (only when TRANSITIONING to Active)
      // EXCEPTIONS:
      // - Already Active: past start date is expected (activation has started)
      // - Suspended: can reactivate if current date is within the date range
      const validationToday = new Date()
      validationToday.setHours(0, 0, 0, 0)
      const selectedStartDate = new Date(startDate)
      const selectedEndDate = endDate ? new Date(endDate) : null
      const isWithinRange = selectedEndDate && validationToday >= selectedStartDate && validationToday <= selectedEndDate
      const isAlreadyActive = activation?.status === 'Active'
      const isReactivatingFromSuspended = activation?.status === 'Suspended' && isWithinRange
      if (selectedStartDate < validationToday && !isAlreadyActive && !isReactivatingFromSuspended) {
        showError(
          'Invalid Start Date',
          'Cannot activate an activation with a start date in the past. Please update the start date.'
        )
        return
      }
    }

    setIsUpdating(true)

    try {
      // First, update the assets
      await setActivationAssets(
        activation.id,
        String(selectedCustomer.id),
        selectedAssetIds
      )

      const updated = await updateActivation(activation.id, String(selectedCustomer.id), {
        name: name.trim(),
        description: description.trim() || null,
        partner_id: partnerId,
        activation_type_id: activationTypeId || null,
        start_date: startDate || null,
        end_date: endDate || null,
        deal_value: dealValue ? parseFloat(dealValue) : null,
        emv_target_value: emvTargetValue ? parseFloat(emvTargetValue) : null,
        notes: notes.trim() || null,
        status,
        metadata: {
          views_cpm: viewsCpm,
          likes_cpm: likesCpm,
          comments_cpm: commentsCpm,
          shares_cpm: sharesCpm
        }
      })

      showSuccess('Activation Updated', `"${updated.name}" has been updated successfully`)

      await loadActivations(String(selectedCustomer.id))

      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Update activation error:', error)
      showError(
        'Failed to update activation',
        errorMessage.includes('already exists')
          ? 'An activation with this name already exists'
          : errorMessage
      )
    } finally {
      setIsUpdating(false)
    }
  }

  const activePartners = partners
    .filter((p) => p.status === 'Active')
    .sort((a, b) => a.name.localeCompare(b.name))

  // Show warning if trying to set Active without required fields
  const showActiveWarning = status === 'Active' && (
    selectedAssetIds.length === 0 || !activationTypeId || !startDate || !endDate
  )

  // Check if trying to activate with a past start date
  // Only show warning when TRANSITIONING to Active, not when already Active
  const formStartDate = startDate ? new Date(startDate) : null
  const todayForValidation = new Date()
  todayForValidation.setHours(0, 0, 0, 0)
  const isTransitioningToActive = status === 'Active' && activation?.status !== 'Active'
  const showPastStartDateWarning = isTransitioningToActive && formStartDate !== null && formStartDate < todayForValidation

  // Business rules for Active activations:
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const activationStartDate = activation?.start_date ? new Date(activation.start_date) : null
  const activationEndDate = activation?.end_date ? new Date(activation.end_date) : null
  const hasStarted = Boolean(activationStartDate && activationStartDate < today)
  const isWithinDateRange = Boolean(activationStartDate && activationEndDate &&
    today >= activationStartDate && today <= activationEndDate)

  const isActiveAndStarted = activation?.status === 'Active' && hasStarted
  const isActiveAndNotStarted = activation?.status === 'Active' && activationStartDate && activationStartDate >= today

  // Status transition rules
  const currentStatus = activation?.status
  const isTerminalState = currentStatus === 'Completed' || currentStatus === 'Inactive'
  const canReactivateSuspended = isWithinDateRange || !hasStarted

  const canBePending = isTerminalState ? false : (currentStatus === 'Pending' || !hasStarted)
  const canBeActive = isTerminalState ? false : (currentStatus === 'Active' || (currentStatus === 'Suspended' ? canReactivateSuspended : currentStatus === 'Pending'))
  const canBeSuspended = isTerminalState ? false : true
  const canBeCompleted = currentStatus === 'Completed' || !isTerminalState
  const canBeInactive = currentStatus === 'Inactive' || !isTerminalState

  // Get today's date in YYYY-MM-DD format for min date attribute
  const todayString = today.toISOString().split('T')[0]

  // Get selected assets for display - combine assets from fullActivation and loaded assets
  const selectedAssets = useMemo(() => {
    const result: Asset[] = []
    const addedIds = new Set<string>()

    // First, add assets from fullActivation.activation_assets (for initially linked assets)
    if (fullActivation?.activation_assets) {
      fullActivation.activation_assets.forEach((aa) => {
        const assetId = aa.asset_id || aa.assets?.id
        if (assetId && selectedAssetIds.includes(assetId) && aa.assets && !addedIds.has(assetId)) {
          // Cast ActivationAsset to Asset - the display only needs the subset of fields
          result.push(aa.assets as unknown as Asset)
          addedIds.add(assetId)
        }
      })
    }

    // Then, add any newly selected assets from the loaded assets list
    assets.forEach((asset) => {
      if (selectedAssetIds.includes(asset.id) && !addedIds.has(asset.id)) {
        result.push(asset)
        addedIds.add(asset.id)
      }
    })

    return result
  }, [fullActivation, selectedAssetIds, assets])

  const availableAssets = assets
    .filter((a) => !selectedAssetIds.includes(a.id))
    .sort((a, b) => a.name.localeCompare(b.name))

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Activation"
      description="Update activation details"
      icon={<Zap className="h-5 w-5" />}
      width="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Validation Messages - At Top */}
        {isActiveAndStarted && (
          <div className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
            <AlertCircle className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">
              This activation has started. Only name, description, notes, and status can be modified.
            </p>
          </div>
        )}

        {isTerminalState && (
          <div className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
            <AlertCircle className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">
              This activation is {currentStatus?.toLowerCase()}. This is a terminal state and the activation cannot be changed.
            </p>
          </div>
        )}

        {currentStatus === 'Suspended' && !isWithinDateRange && hasStarted && (
          <div className="flex items-start gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
            <AlertCircle className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">
              This suspended activation cannot be reactivated because the current date is outside the start and end date range.
            </p>
          </div>
        )}

        {showActiveWarning && (
          <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              To set status to Active: At least one Asset, Activation Type, Start Date, and End Date are required.
            </p>
          </div>
        )}

        {showPastStartDateWarning && !showActiveWarning && (
          <div className="flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded-lg">
            <AlertCircle className="h-4 w-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">
              Cannot activate an activation with a start date in the past. Please update the start date.
            </p>
          </div>
        )}

        {/* Assets Multi-Select - At Top */}
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1">
            Assets {status === 'Active' && <span className="text-red-500">*</span>}
          </label>

          {/* Selected Assets */}
          {selectedAssets.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {selectedAssets.map((asset) => (
                <span
                  key={asset.id}
                  className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full border border-blue-200"
                >
                  <Package className="h-3 w-3" />
                  {asset.name}
                  <button
                    type="button"
                    onClick={() => handleRemoveAsset(asset.id)}
                    disabled={isUpdating || isActiveAndStarted || isTerminalState}
                    className="hover:text-blue-900 disabled:opacity-50"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Asset Selector */}
          <select
            value=""
            onChange={(e) => e.target.value && handleAssetToggle(e.target.value)}
            disabled={isUpdating || isLoadingAssets || availableAssets.length === 0 || isActiveAndStarted || isTerminalState}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
          >
            <option value="">
              {isLoadingAssets
                ? 'Loading assets...'
                : availableAssets.length === 0
                  ? 'No more assets available'
                  : 'Select asset(s)'}
            </option>
            {availableAssets.map((asset) => (
              <option key={asset.id} value={asset.id}>
                {asset.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="name" className="block text-xs font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            id="name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Q1 2026 Content Campaign"
            maxLength={255}
            disabled={isUpdating || isTerminalState}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            autoFocus
          />
        </div>

        <div>
          <label htmlFor="description" className="block text-xs font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe this activation"
            rows={2}
            disabled={isUpdating || isTerminalState}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed resize-none"
          />
        </div>

        {/* Partner and Activation Type - Same Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="partner" className="block text-xs font-medium text-gray-700 mb-1">
              Partner <span className="text-red-500">*</span>
            </label>
            <select
              id="partner"
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              disabled={isUpdating || isActiveAndStarted || isTerminalState}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="">Select partner</option>
              {activePartners.map((partner) => (
                <option key={partner.id} value={partner.id}>
                  {partner.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="type" className="block text-xs font-medium text-gray-700 mb-1">
              Activation Type {status === 'Active' && <span className="text-red-500">*</span>}
            </label>
            <select
              id="type"
              value={activationTypeId}
              onChange={(e) => setActivationTypeId(e.target.value)}
              disabled={isUpdating || isActiveAndStarted || isTerminalState}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="">Select type</option>
              {activationTypes
                .filter((at) => at.is_active)
                .map((at) => (
                  <option key={at.id} value={at.id}>
                    {at.name}
                  </option>
                ))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="startDate" className="block text-xs font-medium text-gray-700 mb-1">
              Start Date {status === 'Active' && <span className="text-red-500">*</span>}
            </label>
            <input
              id="startDate"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              disabled={isUpdating || isActiveAndStarted || isTerminalState}
              min={isActiveAndNotStarted ? todayString : undefined}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <label htmlFor="endDate" className="block text-xs font-medium text-gray-700 mb-1">
              End Date {status === 'Active' && <span className="text-red-500">*</span>}
            </label>
            <input
              id="endDate"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              disabled={isUpdating || isActiveAndStarted || isTerminalState}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="dealValue" className="block text-xs font-medium text-gray-700 mb-1">
              Deal Value
            </label>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">
                $
              </span>
              <input
                id="dealValue"
                type="number"
                step="0.01"
                min="0"
                value={dealValue}
                onChange={(e) => setDealValue(e.target.value)}
                placeholder="0.00"
                disabled={isUpdating || isActiveAndStarted || isTerminalState}
                className="w-full pl-5 pr-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              />
            </div>
          </div>

          <div>
            <label htmlFor="emvTargetValue" className="block text-xs font-medium text-gray-700 mb-1">
              EMV Target
            </label>
            <div className="relative">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs">
                $
              </span>
              <input
                id="emvTargetValue"
                type="number"
                step="0.01"
                min="0"
                value={emvTargetValue}
                onChange={(e) => setEmvTargetValue(e.target.value)}
                placeholder="0.00"
                disabled={isUpdating || isActiveAndStarted || isTerminalState}
                className="w-full pl-5 pr-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
              />
            </div>
          </div>
        </div>

        {/* CPM Multipliers */}
        <div className="border-t border-gray-200 pt-3">
          <h4 className="text-xs font-medium text-gray-700 mb-2">EMV Multipliers</h4>
          <div className="grid grid-cols-2 gap-x-4 gap-y-2">
            <CPMSlider
              label="Views"
              value={viewsCpm}
              onChange={setViewsCpm}
              min={0}
              max={0.03}
              step={0.0005}
              disabled={isUpdating || isActiveAndStarted || isTerminalState}
            />
            <CPMSlider
              label="Likes"
              value={likesCpm}
              onChange={setLikesCpm}
              min={0}
              max={0.03}
              step={0.0005}
              disabled={isUpdating || isActiveAndStarted || isTerminalState}
            />
            <CPMSlider
              label="Comments"
              value={commentsCpm}
              onChange={setCommentsCpm}
              min={0}
              max={0.03}
              step={0.0005}
              disabled={isUpdating || isActiveAndStarted || isTerminalState}
            />
            <CPMSlider
              label="Shares"
              value={sharesCpm}
              onChange={setSharesCpm}
              min={0}
              max={0.03}
              step={0.0005}
              disabled={isUpdating || isActiveAndStarted || isTerminalState}
            />
          </div>
        </div>

        <div>
          <label htmlFor="notes" className="block text-xs font-medium text-gray-700 mb-1">
            Notes
          </label>
          <textarea
            id="notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Additional notes..."
            rows={2}
            disabled={isUpdating || isTerminalState}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed resize-none"
          />
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" className="block text-xs font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value as ActivationStatus)}
            disabled={isUpdating || isTerminalState}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
          >
            {canBePending && <option value="Pending">Pending</option>}
            {canBeActive && <option value="Active">Active</option>}
            {canBeCompleted && <option value="Completed">Completed</option>}
            {canBeSuspended && <option value="Suspended">Suspended</option>}
            {canBeInactive && <option value="Inactive">Inactive</option>}
          </select>
        </div>

        <div className="flex space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isUpdating}
            className="flex-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isUpdating || isTerminalState || !name.trim() || !partnerId || showActiveWarning || showPastStartDateWarning}
            className="flex-1 inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-3.5 w-3.5 mr-1.5" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </Drawer>
  )
}

export default EditActivationDrawer
